import React, { useEffect, useState, useContext, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { withParams } from 'providers/ParamsConsumer';
import {
  computeOfferNextStatesList,
  updateOfferState,
  getCommentsConfig,
  getFolderList,
  resetFolderList
} from 'actions/actionCreators/offer/offerStatesActions';
import { validateStateChange } from 'features/document/edit/documents/actionCreators';
import { List, ListItem, Checkbox, ListItemText, Grid } from '@mui/material';
import { Autocomplete, Button, Dialog, TextField } from '@totalsoft/rocket-ui';
import { changeValueInArray } from 'utils/functions';
import { validationEngine, isMandatory } from 'utils/simple-validations';
import { RightsContext } from 'providers/RightsContext';
import { BoxActionsWrapper } from 'components/common/CommonStyles';

const OfferStatesSelectionListContainer = ({ documentId, siteId, folderSelectionDone, folderList, processing, params }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { readOnlyFull, readOnlyByDocState } = useContext(RightsContext);

  const [showModal, setShowModal] = useState(false);
  const [comment, setComment] = useState({ commentText: '', reasons: [] });
  const [selectedState, setSelectedState] = useState(null);
  const [validationInfo, setValidationInfo] = useState({ isValid: true });
  const [awaitingStateChange, setAwaitingStateChange] = useState(false);
  const [awaitingFolderSelection, setAwaitingFolderSelection] = useState(false);
  const [awaitingComment, setAwaitingComment] = useState(false);
  const [maxLength, setMaxLength] = useState(0);

  const currentState = useSelector(state => {
    return { stateId: state.currentOffer.header.stateId, stateName: state.currentOffer.header.stateName };
  }, shallowEqual);
  const { isLoaded: nextStatesLoaded, nextStatesList: nextStates } = useSelector(state => state.currentOffer.nextStates, shallowEqual);
  const documentTypeId = useSelector(state => state.currentOffer.header.documentTypeId, shallowEqual);
  const { isLoaded: commentsConfigIsLoaded, list: commentsConfig } = useSelector(state => state.dictionaries.commentsConfig, shallowEqual);

  const commentConfigForNextState = useMemo(
    () => commentsConfig?.find(a => a.stateId === selectedState?.stateId),
    [commentsConfig, selectedState]
  );

  const { offerFolderGenerationStateCodes } = params;

  useEffect(() => {
    if (nextStatesLoaded) setMaxLength(nextStates.reduce((max, elem) => Math.max(max, elem.stateName.length), 0));
  }, [nextStatesLoaded, nextStates]);

  useEffect(() => {
    if (!nextStatesLoaded) dispatch(computeOfferNextStatesList(documentId, siteId));
  }, [documentId, siteId, nextStatesLoaded, dispatch]);

  useEffect(() => {
    if (selectedState === null && currentState.stateId) setSelectedState({ ...currentState });
  }, [currentState, selectedState]);

  useEffect(() => {
    if (!commentsConfigIsLoaded && documentTypeId) dispatch(getCommentsConfig(documentTypeId));
  }, [commentsConfigIsLoaded, documentTypeId, dispatch]);

  useEffect(() => {
    if (!folderSelectionDone && awaitingFolderSelection === false && folderList?.length > 0) setAwaitingFolderSelection(true);
    if (awaitingFolderSelection === true && folderSelectionDone) setAwaitingFolderSelection(false);
  }, [folderSelectionDone, folderList, awaitingFolderSelection]);

  const closeModal = useCallback(() => {
    setShowModal(false);
    setAwaitingStateChange(false);
    if (folderList && folderList.length > 0) dispatch(resetFolderList());
  }, [dispatch, folderList]);

  useEffect(() => {
    if (awaitingStateChange && !awaitingFolderSelection && !awaitingComment && selectedState.stateId !== currentState.stateId) {
      if (showModal) closeModal();
      else setAwaitingStateChange(false);
      dispatch(
        updateOfferState(
          documentId,
          siteId,
          selectedState.stateId,
          comment.commentText,
          comment.reasons?.filter(x => x.selected).map(a => a.id) ?? []
        )
      );
    }
    if (awaitingStateChange && !awaitingFolderSelection && awaitingComment && showModal === false) {
      setShowModal(true);
    }
  }, [
    awaitingStateChange,
    awaitingFolderSelection,
    awaitingComment,
    selectedState,
    currentState,
    showModal,
    folderList,
    folderSelectionDone,
    comment,
    dispatch,
    closeModal,
    documentId,
    siteId
  ]);

  useEffect(() => {
    const validationRules =
      commentConfigForNextState && commentConfigForNextState.isVisible && commentConfigForNextState.commentRequired
        ? { commentText: [isMandatory()] }
        : {};
    const validationInfo = validationEngine.validate(comment, validationRules);
    setValidationInfo({
      ...validationInfo
    });
  }, [comment, commentsConfig, selectedState, commentConfigForNextState]);

  const handleSelectState = nextSelectedState => {
    if (
      nextSelectedState.stateId === currentState?.stateId ||
      (nextSelectedState?.requiresConfirmation &&
        (nextSelectedState?.configuredCustomMessage
          ? !window.confirm(nextSelectedState?.configuredCustomMessage)
          : !window.confirm(t('ConfirmStateChange'))))
    )
      return;
    if (
      (!folderSelectionDone || !folderList || folderList.length === 0) &&
      offerFolderGenerationStateCodes.includes(nextSelectedState.stateCode)
    ) {
      dispatch(getFolderList(documentId, siteId, nextSelectedState.stateId));
      setAwaitingFolderSelection(true);
    }

    const cfg = commentsConfig.find(a => a.stateId === nextSelectedState.stateId);
    setComment({
      commentText: '',
      reasons:
        cfg?.reasons?.map(reason => {
          return { ...reason, selected: false };
        }) ?? []
    });

    if (cfg && cfg.isVisible) setAwaitingComment(true);

    setSelectedState(nextSelectedState);
    setAwaitingStateChange(true);
  };

  const onStateChange = nextSelectedState =>
    dispatch(
      validateStateChange(documentId, siteId, currentState?.stateId, nextSelectedState?.stateId, () => handleSelectState(nextSelectedState))
    );

  const onCommentChange = value => {
    setComment({ commentText: value, reasons: comment.reasons });
  };

  const reasonChange = reason => event => {
    let selected = event.target.checked;
    let res = changeValueInArray(comment.reasons, 'id', reason.id, 'selected', selected);
    setComment({ commentText: comment.commentText, reasons: res });
  };

  return (
    <Grid item xs='auto'>
      <Autocomplete
        id={'offerHeader_state'}
        valueKey='stateId'
        labelKey='stateName'
        value={currentState}
        onChange={onStateChange}
        sx={{ minWidth: `${maxLength * 8}px` }}
        isClearable={false}
        isSearchable={false}
        options={nextStates}
        disabled={(readOnlyFull && !readOnlyByDocState) || processing || !commentsConfigIsLoaded}
      />
      <Dialog
        open={showModal}
        onClose={closeModal}
        aria-labelledby='comment-dialog-title'
        title={t('OfferEdit.States.CommentsTitle')}
        content={
          <>
            <TextField
              id={'offerHeader_state_comment'}
              required={commentConfigForNextState?.commentRequired}
              fullWidth
              value={comment.commentText}
              onChange={onCommentChange}
              label={t('OfferEdit.States.Comment')}
              multiline={true}
              error={validationInfo.commentText ? true : false}
              helperText={validationInfo.commentText}
              inputProps={{
                maxLength: 255
              }}
            />
            {comment.reasons && comment.reasons.length > 0 && (
              <List>
                {comment.reasons.map((reason, idx) => (
                  <ListItem key={reason.id} disableGutters={true}>
                    <Checkbox
                      checked={reason.selected}
                      onChange={reasonChange(reason)}
                      id={'offerHeader_state_comment_reason_' + idx + 'selected'}
                    />
                    <ListItemText primary={reason.description} id={'offerHeader_state_comment_reason_' + idx + 'description'} />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        }
        actions={
          <BoxActionsWrapper forDialog>
            <Button
              color='primary'
              variant='contained'
              id={'offerHeader_state_comment_saveBtn'}
              onClick={() => setAwaitingComment(false)}
              disabled={!validationInfo.isValid}
            >
              {t('Save')}
            </Button>
            <Button color='primary' variant='outlined' id={'offerHeader_state_comment_closeBtn'} onClick={closeModal}>
              {t('Close')}
            </Button>
          </BoxActionsWrapper>
        }
      />
    </Grid>
  );
};

OfferStatesSelectionListContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  siteId: PropTypes.number.isRequired,
  processing: PropTypes.bool,
  folderList: PropTypes.array,
  folderSelectionDone: PropTypes.bool,
  params: PropTypes.object.isRequired
};

export default withParams(OfferStatesSelectionListContainer);
